import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { LifeBuoy, ArrowLeft, RefreshCw, Search, ThumbsUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { EmptyState, CardSkeleton } from './common/States';
import { helpAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

/** The little of Markdown the help catalog actually uses: paragraphs, `- `
 *  bullets, `1. ` steps and **bold**.
 *
 *  Built as React elements rather than an HTML string on purpose — help bodies
 *  can be edited straight in the database, so this must not become a way to
 *  inject markup. Anything it does not recognise renders as plain text.
 */
const inlineBold = (text) =>
  String(text).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={i} className="font-medium text-foreground">{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>);

export const HelpBody = ({ body }) => (
  <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
    {String(body || '').split(/\n{2,}/).map((block, i) => {
      const lines = block.split('\n').filter(Boolean);
      const bulleted = lines.length > 0 && lines.every((l) => l.trimStart().startsWith('- '));
      const numbered = lines.length > 0 && lines.every((l) => /^\s*\d+\.\s/.test(l));

      if (bulleted) {
        return (
          <ul key={i} className="list-disc pl-5 space-y-1">
            {lines.map((l, j) => <li key={j}>{inlineBold(l.trimStart().slice(2))}</li>)}
          </ul>
        );
      }
      if (numbered) {
        return (
          <ol key={i} className="list-decimal pl-5 space-y-1">
            {lines.map((l, j) => <li key={j}>{inlineBold(l.replace(/^\s*\d+\.\s/, ''))}</li>)}
          </ol>
        );
      }
      return <p key={i} className="whitespace-pre-line">{inlineBold(block)}</p>;
    })}
  </div>
);

/** Help & FAQs, standalone.
 *
 *  Lifted out of WorkplaceModule so the platform console can show it too: that
 *  module loads announcements, the knowledge base, the directory, the org chart
 *  and referrals on mount, none of which a platform operator has. The help
 *  catalog resolves articles by role, and the platform roles have their own —
 *  which were unreachable while this UI only existed inside a tenant screen.
 */
const HelpArticles = ({ footerNote }) => {
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (params) => {
    try {
      setLoading(true);
      const { data } = await helpAPI.listArticles(params);
      setArticles(data?.articles || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not load help'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (slug) => {
    try {
      const { data } = await helpAPI.getArticle(slug);
      setArticle(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not open that topic'));
    }
  };

  const markHelpful = async (slug) => {
    try {
      const { data } = await helpAPI.markHelpful(slug);
      // The server answers the same way whether this was the first vote or a
      // repeat, so the button settles into the spent state either way rather
      // than depending on which of the two happened.
      setArticle((prev) => (prev ? {
        ...prev,
        helpful_count: data.helpful_count,
        marked_helpful: true,
      } : prev));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not record that'));
    }
  };

  if (article) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="ghost" size="sm" onClick={() => setArticle(null)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> All topics
        </Button>
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle>{article.title}</CardTitle>
            {article.category && <Badge variant="outline" className="w-fit">{article.category}</Badge>}
          </CardHeader>
          <CardContent className="space-y-6">
            <HelpBody body={article.body} />
            <Button
              variant="outline"
              size="sm"
              disabled={Boolean(article.marked_helpful)}
              onClick={() => markHelpful(article.slug)}
            >
              <ThumbsUp className="h-4 w-4 mr-1" />
              {article.marked_helpful ? 'You found this helpful' : 'This helped'}
              {article.helpful_count ? ` (${article.helpful_count})` : ''}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Grouped for display only; the server already decided which topics this
  // person's role and vertical should see.
  const categories = [...new Set(articles.map((a) => a.category).filter(Boolean))];
  const shown = category === 'all' ? articles : articles.filter((a) => a.category === category);

  return (
    <div className="space-y-6 animate-fade-in">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <LifeBuoy className="h-4 w-4 text-muted-foreground" />
              Help &amp; FAQs
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              How this works, written for your role and your industry.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => load()}><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          {loading && <CardSkeleton rows={4} />}
          {!loading && !articles.length && <EmptyState compact title="No help topics available yet." />}
          {!loading && articles.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  placeholder="Search help"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && load(query ? { q: query } : undefined)}
                />
                <Button variant="outline" onClick={() => load(query ? { q: query } : undefined)}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>

              {categories.length > 1 && (
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    variant={category === 'all' ? 'default' : 'outline'}
                    onClick={() => setCategory('all')}
                  >
                    All
                  </Button>
                  {categories.map((c) => (
                    <Button
                      key={c}
                      size="sm"
                      variant={category === c ? 'default' : 'outline'}
                      onClick={() => setCategory(c)}
                    >
                      {c}
                    </Button>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                {shown.map((a) => (
                  <button
                    key={a.slug}
                    type="button"
                    onClick={() => open(a.slug)}
                    className="w-full rounded-lg border border-border p-3 text-left hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-medium text-sm">{a.title}</span>
                      {a.category && <Badge variant="secondary" className="shrink-0">{a.category}</Badge>}
                    </div>
                  </button>
                ))}
              </div>

              {footerNote && <p className="text-xs text-muted-foreground">{footerNote}</p>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpArticles;
